/**
 * Currency detection utilities for pricing page
 * Uses Cloudflare geolocation when available, falls back to browser locale
 */

export type Currency = 'USD' | 'EUR' | 'GBP' | 'AUD';

const REGION_TO_CURRENCY: Record<string, Currency> = {
  // North America
  'US': 'USD',
  'CA': 'USD', //  Canadian customers typically see USD pricing
  'MX': 'USD',

  // United Kingdom
  'GB': 'GBP',

  // Australia & New Zealand
  'AU': 'AUD',
  'NZ': 'AUD',

  // European Union
  'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR',
  'FR': 'EUR', 'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR',
  'LV': 'EUR', 'LT': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR',
  'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'ES': 'EUR', 'HR': 'EUR',

  // Other European countries using EUR
  'AD': 'EUR', 'MC': 'EUR', 'SM': 'EUR', 'VA': 'EUR',
};

/**
 * Detect currency based on user location
 * Priority:
 * 1. Cloudflare geolocation (server-side, most accurate)
 * 2. Browser navigator.language (client-side fallback)
 * 3. Default to USD
 */
export async function detectCurrency(): Promise<Currency> {
  // Try Cloudflare geolocation API first
  try {
    const response = await fetch('/api/public/geo');
    if (response.ok) {
      const data = await response.json();
      const country = data.country as string | undefined;
      if (country && REGION_TO_CURRENCY[country]) {
        return REGION_TO_CURRENCY[country];
      }
    }
  } catch (error) {
    // Geolocation API failed, fall through to browser detection
    console.debug('Geolocation API unavailable, using browser locale');
  }

  // Fallback to browser locale
  return detectCurrencyFromLocale();
}

/**
 * Detect currency from browser locale
 */
export function detectCurrencyFromLocale(): Currency {
  try {
    const locale = navigator.language || 'en-US';
    const region = locale.split('-')[1]?.toUpperCase();

    if (region && REGION_TO_CURRENCY[region]) {
      return REGION_TO_CURRENCY[region];
    }
  } catch {
    // Ignore errors
  }

  return 'USD';
}
